import type { AssistantLanguage, AssistantSuggestion } from '@chat-emp/shared';

import type { ApplicationStatus } from '../fixtures/domain.js';
import { track } from '../lib/analytics.js';
import type { CardAction } from './assistantActions.js';
import { isRecommendedDraft, type DraftTranslation } from './AssistantCard.js';

/**
 * The assistant's own events.
 *
 * Named apart from the quick-reply lane on purpose. Two cards are live at once
 * across the employer base during the rollout, and the question the rollout
 * exists to answer is whether the new one is better — which is unanswerable if
 * both lanes report under `quick_reply_used`.
 *
 * No property is called `source`: the BFF stamps a top-level `source:
 * 'chat_emp'` that the backend copies over any event property of the same name,
 * which is why the legacy lane sends `reply_source`.
 *
 * Tracking is never load-bearing. `track` queues and flushes on its own and
 * throws nothing back here.
 */

/**
 * Which surface the card was drawn on.
 *
 * Passed by the caller, never inferred: a component that works out where it is
 * rendering is one that works it out wrong inside an iframe. The widget and the
 * page draw the same card from the same hook, so without this every assistant
 * row in PostHog answers for both at once — and whether a reading spent in the
 * widget gets used is the question the widget's card exists to answer.
 */
export type AssistantSurface = 'chat' | 'widget' | 'pane';

interface Shown {
  suggest: AssistantSuggestion | null;
  status: ApplicationStatus;
  surface: AssistantSurface;
}

/** Which of the card's three faces the employer was actually shown. */
function stateOf(suggest: AssistantSuggestion | null): 'decision' | 'message' | 'actions' {
  if (suggest === null) return 'actions';
  return suggest === 'message' ? 'message' : 'decision';
}

function cardProperties({ suggest, status, surface }: Shown) {
  return {
    state: stateOf(suggest),
    ...(suggest ? { suggested: suggest } : {}),
    application_status: status,
    surface,
  };
}

export function trackAssistantShown({ suggest, status, surface }: Shown): void {
  const properties = cardProperties({ suggest, status, surface });
  track('assistant_card_shown', properties);

  // Its own event rather than "the absence of the others": how often the
  // assistant says nothing is the number that decides whether the reading needs
  // richer grounding, and a number worth acting on is worth counting directly.
  if (properties.state === 'actions') {
    track('assistant_reading_empty', { application_status: status, surface });
  }
}

export type OpenedBy = 'header' | 'read_more';

interface Toggled extends Shown {
  open: boolean;
  reading: boolean;
  by?: OpenedBy;
}

/** Nothing counts mid-reading: the card shuts itself then, and has no face to report. */
export function trackAssistantToggled({ open, reading, by = 'header', ...shown }: Toggled): void {
  if (reading) return;
  const properties = cardProperties(shown);
  if (open) track('assistant_card_opened', { ...properties, opened_by: by });
  else track('assistant_card_closed', properties);
}

interface Taken {
  action?: CardAction;
  draftIndex?: number;
  /** Whether this was the row the assistant actually recommended. */
  recommended: boolean;
  by: 'press' | 'shortcut';
  surface: AssistantSurface;
}

export function trackAssistantTaken({ action, draftIndex, recommended, by, surface }: Taken): void {
  if (draftIndex !== undefined) {
    track('assistant_draft_used', {
      draft_index: draftIndex,
      recommended,
      taken_by: by,
      surface,
    });
    return;
  }
  if (!action) return;
  track('assistant_action_taken', { action, recommended, taken_by: by, surface });
}

interface Translated {
  language: AssistantLanguage;
  from?: AssistantLanguage;
  draftIndex: number;
  recommended: boolean;
  surface: AssistantSurface;
}

export function trackAssistantTranslated({
  language,
  from,
  draftIndex,
  recommended,
  surface,
}: Translated): void {
  track('assistant_draft_translated', {
    language,
    ...(from ? { from_language: from } : {}),
    draft_index: draftIndex,
    recommended,
    surface,
  });
}

/** The card's language switch, reporting each press before it translates. */
export function trackingTranslation(
  translation: DraftTranslation | undefined,
  recommended: number | null,
  surface: AssistantSurface,
): DraftTranslation | undefined {
  if (!translation) return undefined;
  return {
    ...translation,
    onTranslate: (index, language) => {
      trackAssistantTranslated({
        language,
        from: translation.languages[index],
        draftIndex: index,
        recommended: isRecommendedDraft(recommended, index, translation.languages.length),
        surface,
      });
      translation.onTranslate(index, language);
    },
  };
}
